import { fetchCurrentUser } from '../../ducks/user.duck';
import { denormalisedEntities, updatedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { entityRefs } from '../../util/helperFunctions';
import { createImageVariantConfig } from '../../util/sdkLoader';

// ================ Action types ================ //
const ActionTypes = {
  FETCH_LISTINGS_REQUEST: 'app/ShippingDetailsPage/FETCH_LISTINGS_REQUEST',
  FETCH_LISTINGS_SUCCESS: 'app/ShippingDetailsPage/FETCH_LISTINGS_SUCCESS',
  FETCH_LISTINGS_ERROR: 'app/ShippingDetailsPage/FETCH_LISTINGS_ERROR',

  UPDATE_LISTING_REQUEST: 'app/ShippingDetailsPage/UPDATE_LISTING_REQUEST',
  UPDATE_LISTING_SUCCESS: 'app/ShippingDetailsPage/UPDATE_LISTING_SUCCESS',
  UPDATE_LISTING_ERROR: 'app/ShippingDetailsPage/UPDATE_LISTING_ERROR',
};

// ================ Reducer ================ //

const initialState = {
  listings: [],
  fetchListingsInProgress: false,
  fetchListingsError: null,
  updateListingInProgress: false,
  updateListingError: null,
};

export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case ActionTypes.FETCH_LISTINGS_REQUEST:
      return { ...state, fetchListingsInProgress: true, fetchListingsError: null };
    case ActionTypes.FETCH_LISTINGS_SUCCESS:
      return { ...state, fetchListingsInProgress: false, listings: action.payload };
    case ActionTypes.FETCH_LISTINGS_ERROR:
      return { ...state, fetchListingsInProgress: false, fetchListingsError: action.payload };

    case ActionTypes.UPDATE_LISTING_REQUEST:
      return { ...state, updateListingInProgress: true, updateListingError: null };
    case ActionTypes.UPDATE_LISTING_SUCCESS:
      return { ...state, updateListingInProgress: false };
    case ActionTypes.UPDATE_LISTING_ERROR:
      return { ...state, updateListingInProgress: false, updateListingError: action.payload };
  }
  return state;
}

// ================ Selectors ================ //
export const listingsSelector = state => state.ShippingDetailsPage.listings;

// ================ Action creators ================ //

export const fetchListingsRequest = () => ({
  type: ActionTypes.FETCH_LISTINGS_REQUEST,
});

export const fetchListingsSuccess = response => ({
  type: ActionTypes.FETCH_LISTINGS_SUCCESS,
  payload: response,
});

export const fetchListingsError = e => ({
  type: ActionTypes.FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const updateListingRequest = () => ({
  type: ActionTypes.UPDATE_LISTING_REQUEST,
});

export const updateListingSuccess = () => ({
  type: ActionTypes.UPDATE_LISTING_SUCCESS,
});

export const updateListingError = e => ({
  type: ActionTypes.UPDATE_LISTING_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const fetchOwnListings = (params = {}, config) => async (dispatch, getState, sdk) => {
  dispatch(fetchListingsRequest(params));

  try {
    const { aspectWidth = 1, aspectHeight = 1, variantPrefix = 'listing-card' } =
      config?.layout?.listingImage || {};

    const aspectRatio = aspectHeight / aspectWidth;

    // Base query configuration (shared across all pages)
    const baseQuery = {
      page: params.page || 1,
      perPage: params.batchSize || 20, // Default batch size if not provided
      states: 'published,closed,draft',
      include: ['images', 'currentStock', 'author'],
      'fields.image': [
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
        'variants.default',
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'fields.listing': ['title', 'description', 'price', 'deleted', 'state', 'publicData'],
      ...params.rest, // Any additional params passed by caller
    };

    // We'll collect ALL listings here
    const allListings = [];
    let entities = {};
    let currentPage = baseQuery.page;

    // Fetch first page to get totalPages
    const firstResponse = await sdk.ownListings.query(baseQuery);
    const { data: firstPageData, meta } = firstResponse.data || {};
    const totalPages = meta?.totalPages || 1;

    // Process first page
    entities = updatedEntities({}, firstResponse.data);
    allListings.push(...denormalisedEntities(entities, entityRefs(firstPageData || []), false));

    // Fetch remaining pages (if any)
    for (currentPage = baseQuery.page + 1; currentPage <= totalPages; currentPage++) {
      const response = await sdk.ownListings.query({
        ...baseQuery,
        page: currentPage,
      });

      entities = updatedEntities(entities, response.data);
      allListings.push(
        ...denormalisedEntities(entities, entityRefs(response.data?.data || []), false)
      );

      // Small delay to avoid rate limiting (optional but helpful)
      await new Promise(resolve => setTimeout(resolve, 150));
    }

    // Final success dispatch with the complete array
    dispatch(fetchListingsSuccess(allListings));
    return allListings; // Return full list for potential caller usage
  } catch (err) {
    const storableErr = storableError(err);
    dispatch(fetchListingsError(storableErr));
    throw storableErr;
  }
};

export const updateListing = params => async (dispatch, getState, sdk) => {
  dispatch(updateListingRequest());

  try {
    const { ids, shippingPriceInSubunitsOneItem } = params;
    // Early return if no ids array or empty array
    if (!ids || !Array.isArray(ids) || ids.length === 0) {
      dispatch(updateListingSuccess());
      return;
    }

    const batchSize = 50;

    // Split ids into batches of 50
    for (let i = 0; i < ids.length; i += batchSize) {
      const batch = ids.slice(i, i + batchSize);

      // Create array of update promises for current batch
      const updatePromises = batch.map(idObj =>
        sdk.ownListings.update(
          {
            id: idObj,
            publicData: {
              shippingPriceInSubunitsOneItem: shippingPriceInSubunitsOneItem?.amount,
            },
          },
          { expand: true }
        )
      );

      // Execute all updates in current batch concurrently
      await Promise.all(updatePromises);

      // console.log(
      //   `Batch ${Math.floor(i / batchSize) + 1} completed: ${batch.length} listings updated`
      // );
    }

    dispatch(updateListingSuccess());
  } catch (e) {
    dispatch(updateListingError(storableError(e)));
    throw e;
  }
};

export const loadData = (params, search, config) => async (dispatch, getState, sdk) => {
  await dispatch(fetchOwnListings(params, config));
  return fetchCurrentUser();
};
